import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "~/components/common/base-link/context";
import { ContentItemOlympicsTopStory } from "~/components/content/content-item-render/variants/olympics-top-story";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { getAbsoluteUrl } from "~/lib/utils";
import type { topStoriesOlympicQueueItemsEdge$key } from "~/queries/__generated__/topStoriesOlympicQueueItemsEdge.graphql";

import { Container, Header, TopStories } from "./styles";

type Props = {
  className?: string;
  pageType: "section" | "article";
  reference?: topStoriesOlympicQueueItemsEdge$key;
};

export const OlympicTopStories: FunctionComponent<Props> = ({
  className,
  pageType,
  reference: reference_,
}) => {
  const topStories = useFragment(
    graphql`
      fragment topStoriesOlympicQueueItemsEdge on QueueItemsEdge @relay(plural: true) {
        node {
          ... on Content {
            ...olympicsTopStoryContentItemContent
          }
        }
      }
    `,
    reference_,
  );

  const sendTrackingHandler = (entityId?: string, urlAlias?: string, index?: number) => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        article_seq: `${index}`,
        destination_url: getAbsoluteUrl(urlAlias),
        page_type: pageType,
        widget_name: "olympics2024",
      },
      subcategory: "widget",
    });
  };

  return (
    <Container className={className}>
      <Header>Top Stories</Header>
      <TopStories>
        {topStories?.map(({ node }, index) => (
          <BaseLinkContextProvider
            customQueryParameters={{
              module: "olympics2024",
              pgtype: pageType === "section" ? "sub-section" : pageType,
            }}
            key={index}
          >
            <ContentItemOlympicsTopStory
              reference={node}
              onClick={(entityId, urlAlias) => sendTrackingHandler(entityId, urlAlias, index + 1)}
            />
          </BaseLinkContextProvider>
        ))}
      </TopStories>
    </Container>
  );
};

OlympicTopStories.displayName = "OlympicTopStories";
